import { CUSTOM_HEADERS } from '@federa/const/http-header-names';
import { HTTP_METHODS } from '@federa/const/http-methods';
import { ROOM_API_ROUTES } from '@federa/const/routes-api';
import { CHATROOM_FETCH_PATHS } from '@federa/const/chatroom-routes';
import { JSON_HEADERS } from '@federa/const/http-headers';
import { buildAdapterError } from '@federa/const/adapter-envelopes';
import { asRecordOrUndefined, rowsOrEmpty } from '@federa/const/record-guards';
import { requestFailedWithStatus } from '@federa/const/error-messages';
import { INTERNAL_API_ROUTES } from '@federa/const/internal-routes';
import { LOGGER_TAGS } from '@federa/const/logging';
import { buildJsonPostInit } from '@federa/const/http-request-init';
import { isSuccess } from '@federa/const/http-status';
import { scopedLogger } from '@federa/utils/deps/registry';
import { checkAndParseResponse } from '@federa/utils/http/parse-json-body';
import { sortByCreatedAtAsc } from '@federa/utils/dates/timestamp';
import { getErrorMessage } from '@federa/utils/errors/error-core';
import { isNonNull } from '@federa/utils/validation/type-guards';
import { resolveEnvelopeSuccess } from '@federa/components/lib/api/api-envelope';
import { platformFetch } from '@federa/utils/http/platform-fetch';
import { toChatMessage } from './messagingClient.parsers.ts';
import { toChatRoom, collectRooms } from './messaging-mappers.ts';
import type { ApiResponse } from '@federa/types/api-response';
import type { ChatMessageStruct, ChatRoomStruct } from '@federa/types/conversations-chat-structs';
import type { MessagingTransport, RawChatRoom, RawConversationResponse } from '@federa/types/ui-chat-messaging';

const log = scopedLogger(LOGGER_TAGS.FETCH_RECENT_MESSAGES);

/**
 * Build the SUCCESS envelope for a body that has already cleared `parseApiResponse`.
 *
 * NAMED FOR THE ONE ARM IT BUILDS. It used to be `buildApiResponse` and picked its union variant off the status code,
 * but both callers guard on `parsed.ok` first and `checkAndParseResponse` answers `ok: false` for EVERY non-2xx -- so
 * `isSuccess(status)` was true on every call that reached it and the `success: false` branch was DEAD. That branch is
 * deleted rather than left as an unreachable second spelling of the failure envelope its callers already own; the
 * failure sentence now has exactly one producer, `parseApiResponse` below.
 */
function buildSuccessResponse<T>(status: number, data: T, raw: unknown): ApiResponse<T> {
	return { success: true, status, data, raw };
}

/**
 * The named row array out of a ChatRoom DO list body.
 *
 * EVERY DO list route answers a KEYED OBJECT -- `{"participants": [...]}`, `{"messages": [...]}` -- and never a bare
 * array; `checkAndParseResponse` unwraps nothing, so `parsed.body` IS that object. A reader that tests
 * `Array.isArray(parsed.body)` instead is false for every response the DO can produce, so its list is permanently `[]`
 * under a `success: true` envelope. `fetchRecentMessages` reads the key.
 */
function bodyRows(body: unknown, key: string): Record<string, unknown>[] {
	return rowsOrEmpty(asRecordOrUndefined(body)?.[key]);
}

/**
 * Parse a fetch response with standard error handling. Returns the parsed body, or the ONE failure envelope this
 * client produces.
 *
 * THE TWO FAILURE ARMS ARE NOT THE SAME FAILURE, and conflating them is what put raw JSON on a user's screen.
 * `checkAndParseResponse` answers `ok: false` for both, but its `error` member means different things:
 *
 *   - NON-2XX: `error` is the service's body TEXT, braces included -- `extractErrorText` short-circuits before any
 *     JSON read. A caller rendering it into a toast showed `{"message":"no access to this account"}` on a 403. The
 *     user-facing sentence for that case is the `requestFailedWithStatus` SSOT, and the body text is preserved
 *     verbatim on `raw` so nothing is lost to logs or to a caller that wants to inspect it.
 *   - OK-BUT-UNPARSEABLE: `error` is already a composed sentence and the status is a synthesized 500. That sentence is
 *     kept as-is; `requestFailedWithStatus(response.status)` would read "Request failed with status 200", which is a
 *     lie about a response that arrived fine and then failed to parse.
 *
 * `result.status` rather than `response.status` on the envelope, so the synthesized 500 rides out on the parse arm.
 */
async function parseApiResponse(
	response: Response,
): Promise<{ ok: true; status: number; body: unknown } | { ok: false; response: ApiResponse<never> }> {
	const status = response.status;
	const result = await checkAndParseResponse(response);
	if (!result.ok) {
		const transportFailed = !isSuccess(status);
		const message = transportFailed ? requestFailedWithStatus(status) : result.error;
		return {
			ok: false,
			response: {
				success: false,
				status: result.status,
				error: message,
				message,
				raw: result.error,
			},
		};
	}
	return { ok: true, status, body: result.data };
}

interface FetchConversationsParams {
	userId: string;
	role: string;
}

interface CreateConversationParams {
	userId: string;
	friendlyName: string;
	role: string;
	conversationType: string;
}

/** Every live caller passes an absolute chats-service base; the undefined-transport default is a DEAD fallback -- the reverse proxy it points at does not exist. */
function resolveTransport(transport?: MessagingTransport): {
	base: string;
	headers: Record<string, string>;
	fetchFn: (input: string, init?: RequestInit) => Promise<Response>;
} {
	return {
		base: transport?.base ?? INTERNAL_API_ROUTES.CONVERSATIONS,
		headers: transport?.headers ?? {},
		// Component-purity seam: network I/O is injectable via the transport; the default takes the shared platform
		// fetch so existing hosts need no change.
		fetchFn: transport?.fetchFn ?? platformFetch,
	};
}

function withTransportHeaders(init: RequestInit, headers: Record<string, string>): RequestInit {
	return { ...init, headers: { ...(init.headers as Record<string, string>), ...headers } };
}

export async function fetchConversations(
	params: FetchConversationsParams,
	transport?: MessagingTransport,
): Promise<ApiResponse<ChatRoomStruct[]>> {
	const t = resolveTransport(transport);
	const endpoint = `${t.base}/conversations`;

	const response = await t.fetchFn(endpoint, {
		method: HTTP_METHODS.POST,
		headers: {
			...JSON_HEADERS,
			[CUSTOM_HEADERS.USER_ID]: params.userId,
			[CUSTOM_HEADERS.USER_ROLE]: params.role,
			...t.headers,
		},
	});

	const parsed = await parseApiResponse(response);
	if (!parsed.ok) return parsed.response;

	const rooms = collectRooms(parsed.body as RawConversationResponse);
	return buildSuccessResponse(parsed.status, rooms, parsed.body);
}

export async function createConversation(
	params: CreateConversationParams,
	transport?: MessagingTransport,
): Promise<ApiResponse<ChatRoomStruct>> {
	const t = resolveTransport(transport);
	const endpoint = `${t.base}${ROOM_API_ROUTES.CREATE}`;

	const payload = {
		friendlyName: params.friendlyName,
		userId: params.userId,
		role: params.role,
		conversationType: params.conversationType,
		referenceId: params.userId, // Ensure referenceId is set to userId for proper duplicate detection
	};

	const response = await t.fetchFn(endpoint, withTransportHeaders(buildJsonPostInit(payload), t.headers));

	const parsed = await parseApiResponse(response);
	if (!parsed.ok) return parsed.response;

	// Tag-or-status rule shared with postJsonRoute; the shared reader falls back to the transport status, where an inline cast threw on a null body.
	const envelopeSuccess = resolveEnvelopeSuccess(parsed.body, parsed.status);
	const message = (parsed.body as { message?: string }).message;
	const room = toChatRoom(parsed.body as RawChatRoom);

	// Validate that the room has a valid ID - an empty response {} produces id: ""
	const hasValidRoom = Boolean(room.id);

	if (envelopeSuccess && hasValidRoom) {
		return { success: true, status: parsed.status, message, data: room, raw: parsed.body };
	}
	const failureMessage = message || 'Server returned invalid room data';
	// `error` and `message` both carried failureMessage here, which is exactly buildAdapterError's contract (`error`
	// mirrors `message`); `raw` is the ApiResponse-only transport field the atom does not model, so it rides alongside.
	return { ...buildAdapterError(failureMessage, parsed.status), raw: parsed.body };
}

export async function fetchRecentMessages(
	roomId: string,
	opts?: { batchCount?: number },
	transport?: MessagingTransport,
): Promise<ApiResponse<ChatMessageStruct[]>> {
	const t = resolveTransport(transport);
	// Segment imported from the ChatRoom DO's fetch-route SSOT, not hand-spelled: the camelCase form 404'd here once, and a copied literal would 404 again on rename.
	const endpoint = `${t.base}${ROOM_API_ROUTES.PREFIX}${roomId}${CHATROOM_FETCH_PATHS.LOAD_PREVIOUS_MESSAGES}`;

	try {
		const res = await t.fetchFn(endpoint, withTransportHeaders(buildJsonPostInit({ batchCount: opts?.batchCount ?? 1 }), t.headers));

		const parsed = await parseApiResponse(res);
		if (!parsed.ok) return parsed.response;

		// The keyed reader -- the cast-then-Array.isArray pair it replaced is what let the deleted participants sibling
		// drift onto a different (and wrong) resolution of the identical body.
		const raw = bodyRows(parsed.body, 'messages');
		const filtered = raw.map((m) => toChatMessage(m, roomId)).filter(isNonNull);
		const messages = sortByCreatedAtAsc(filtered);
		if (isSuccess(parsed.status)) {
			return { success: true, status: parsed.status, data: messages, raw: parsed.body };
		}
		return {
			success: false,
			status: parsed.status,
			error: requestFailedWithStatus(parsed.status),
			raw: parsed.body,
		};
	} catch (error) {
		log.error('Failed to fetch messages', { error: getErrorMessage(error) });
		// Status 0 is the no-transport sentinel this catch already used; `error`/`message` were two calls returning
		// the same string, which is buildAdapterError's mirror contract.
		return buildAdapterError(getErrorMessage(error), 0);
	}
}
